import React, { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";

export default function NotificationBell({ role = "customer", testid = "notification-bell" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const isAdmin = role === "admin";
  const fetchUrl = isAdmin ? "/admin/notifications" : "/notifications/me";

  const load = async () => {
    try {
      const { data } = await api.get(fetchUrl);
      setItems(Array.isArray(data) ? data : []);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch {}
  };

  const markRead = async (n) => {
    if (n.read) return;
    try {
      const url = isAdmin ? `/admin/notifications/${n.id}/read` : `/notifications/${n.id}/read`;
      await api.post(url);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    } catch {}
  };

  const linkFor = (n) => {
    if (!n.ref_id) return null;
    if (isAdmin && n.type === "new_account") return `/admin/users`;
    if (isAdmin && n.type === "new_order") return `/admin/orders`;
    if (isAdmin && (n.type === "proof_uploaded" || n.type === "order_status")) return `/admin/orders`;
    if (role === "driver") return `/driver/delivery/${n.ref_id}`;
    if (role === "customer") {
      // Delivered / photo / past-states deep-link to order history;
      // anything still in progress goes to live track map.
      if (["proof_uploaded", "order_completed"].includes(n.type) ||
          (n.title || "").toLowerCase().includes("delivered")) {
        return `/customer/orders?order=${n.ref_id}`;
      }
      return `/track/${n.ref_id}`;
    }
    return null;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className="relative h-10 w-10 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-700"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EA580C] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl border border-stone-200 shadow-xl overflow-hidden z-50" data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
            <div className="font-display font-bold text-stone-900">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all-read" className="text-xs font-bold text-[#166534] hover:text-[#14532d] flex items-center gap-1">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-stone-500 text-sm">No notifications yet.</div>
            ) : (
              items.map((n) => {
                const link = linkFor(n);
                const body = (
                  <div onClick={() => markRead(n)} className={`px-4 py-3 border-b border-stone-100 hover:bg-stone-50 cursor-pointer ${!n.read ? "bg-orange-50/40" : ""}`} data-testid={`notif-item-${n.id}`}>
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#EA580C] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-stone-900 truncate">{n.title}</div>
                        <div className="text-xs text-stone-600 mt-0.5 line-clamp-2">{n.message}</div>
                        <div className="text-[10px] text-stone-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                      </div>
                    </div>
                  </div>
                );
                return link ? (
                  <Link key={n.id} to={link} onClick={() => setOpen(false)}>{body}</Link>
                ) : (
                  <div key={n.id}>{body}</div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
